/**
 * Sales Performance round 2, stage 2: the alerts. Pure and side-effect free, so the browser computes them today
 * and stage 5's backend can share the code if delivery to the bell is ever switched on.
 *
 * It only DECIDES what is worth a look. It writes nothing and sends nothing.
 *
 * Input is the reach history of each salesperson-category (and of the company's categories) over COMPLETE months
 * only, plus the training records (an empty list until stage 3 stores any).
 */
import { ragStatus } from './sales-performance';

export interface AlertPoint {
    key: string;
    /** first and last day of the month, YYYY-MM-DD */
    start: string;
    end: string;
    /** reach, 0..1 */
    value: number;
}

export interface AlertSeries {
    /** null for the company's own series */
    rep: string | null;
    repName: string;
    category: string;
    categoryName: string;
    /** complete months, oldest first; the last is the month being judged */
    points: AlertPoint[];
    /** the (possibly weighted) target the series is judged against */
    target: number | null;
    /** too few active customers for a share to mean much: no alert */
    fewCustomers?: boolean;
}

export interface AlertTraining {
    id: string;
    rep: string;
    category: string;
    status: 'assigned' | 'completed' | 'cancelled';
    /** YYYY-MM-DD */
    dueAt?: string;
    completedAt?: string;
}

export type AlertKind = 'turned-red' | 'declining' | 'training-overdue' | 'training-low';

/** Display order of the groups. */
export const ALERT_KINDS: { kind: AlertKind; label: string }[] = [
    { kind: 'turned-red', label: 'Turned red' },
    { kind: 'declining', label: 'Down three months running' },
    { kind: 'training-overdue', label: 'Training overdue' },
    { kind: 'training-low', label: 'Still low after training' },
];

export interface SpAlert {
    id: string;
    kind: AlertKind;
    rep: string | null;
    repName: string;
    category: string | null;
    categoryName: string;
    month: string | null;
    text: string;
}

export interface AlertInput {
    series: AlertSeries[];
    training: AlertTraining[];
    /** YYYY-MM-DD, passed in so the function stays pure */
    today: string;
}

/** A decline counts only if every month fell, it started at 5% or more, and it lost at least 3 points overall. */
export const DECLINE_MIN_START = 0.05;
export const DECLINE_MIN_FALL = 0.03;
const EPS = 1e-9;
const isRealDecline = (run: number[]) =>
    run.every((v, i) => i === 0 || v < run[i - 1]) &&
    run[0] >= DECLINE_MIN_START - EPS &&
    run[0] - run[run.length - 1] >= DECLINE_MIN_FALL - EPS;

const pctText = (x: number) => `${Math.round(x * 100)}%`;

export function salesPerformanceAlerts(input: AlertInput): SpAlert[] {
    const out: SpAlert[] = [];
    const redOnes = new Set<string>();
    const who = (s: AlertSeries) => (s.rep === null ? 'The company' : s.repName);

    for (const s of input.series) {
        if (s.fewCustomers || !s.points.length) continue;
        const values = s.points.map((p) => p.value);
        const last = s.points[s.points.length - 1];
        const id = (kind: AlertKind) => `${kind}|${s.rep ?? '-'}|${s.category}`;

        if (s.rep !== null && s.target !== null && ragStatus(values, s.target) === 'red') {
            const before = values.length > 1 ? ragStatus(values.slice(0, -1), s.target) : 'none';
            if (before !== 'red') {
                redOnes.add(id('declining'));
                out.push({
                    id: id('turned-red'),
                    kind: 'turned-red',
                    rep: s.rep,
                    repName: s.repName,
                    category: s.category,
                    categoryName: s.categoryName,
                    month: last.key,
                    text: `${s.repName}: ${s.categoryName} is ${pctText(last.value)}, against a target of ${pctText(s.target)}.`,
                });
            }
        }

        if (values.length >= 4 && !redOnes.has(id('declining'))) {
            const run = values.slice(-4);
            if (isRealDecline(run))
                out.push({
                    id: id('declining'),
                    kind: 'declining',
                    rep: s.rep,
                    repName: s.repName,
                    category: s.category,
                    categoryName: s.categoryName,
                    month: last.key,
                    text: `${who(s)}: ${s.categoryName} has fallen three months running, ${pctText(run[0])} to ${pctText(run[3])}.`,
                });
        }
    }

    for (const t of input.training) {
        if (t.status === 'assigned' && t.dueAt && t.dueAt < input.today) {
            const s = input.series.find((x) => x.rep === t.rep && x.category === t.category);
            out.push({
                id: `training-overdue|${t.id}`,
                kind: 'training-overdue',
                rep: t.rep,
                repName: s?.repName ?? `Rep ${t.rep}`,
                category: t.category,
                categoryName: s?.categoryName ?? t.category,
                month: null,
                text: `${s?.repName ?? `Rep ${t.rep}`}: training on ${s?.categoryName ?? t.category} was due ${t.dueAt}.`,
            });
        }
        if (t.status === 'completed' && t.completedAt) {
            const s = input.series.find((x) => x.rep === t.rep && x.category === t.category);
            if (!s || s.target === null) continue;
            const after = s.points.filter((p) => p.start > (t.completedAt as string));
            const latest = after[after.length - 1];
            if (latest && latest.value < s.target)
                out.push({
                    id: `training-low|${t.id}`,
                    kind: 'training-low',
                    rep: t.rep,
                    repName: s.repName,
                    category: t.category,
                    categoryName: s.categoryName,
                    month: latest.key,
                    text: `${s.repName}: ${s.categoryName} is still ${pctText(latest.value)} after training, against a target of ${pctText(s.target)}.`,
                });
        }
    }

    const order = (k: AlertKind) => ALERT_KINDS.findIndex((x) => x.kind === k);
    return out.sort(
        (a, b) =>
            order(a.kind) - order(b.kind) ||
            a.repName.localeCompare(b.repName) ||
            a.categoryName.localeCompare(b.categoryName)
    );
}
